import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { SUPPORTED_CEX } from '../constants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface ApiSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ApiSyncModal: React.FC<ApiSyncModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [selectedCex, setSelectedCex] = useState<string>('');
  const [accountLabel, setAccountLabel] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [apiSecret, setApiSecret] = useState<string>('');
  const [apiPassphrase, setApiPassphrase] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const selectedCexConfig = SUPPORTED_CEX.find(cex => cex.value === selectedCex);
  const requiresPassphrase = selectedCexConfig?.requiresPassphrase || false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCex || !apiKey || !apiSecret) {
      setError('Please fill in all required fields');
      return;
    }

    if (requiresPassphrase && !apiPassphrase) {
      setError(`${selectedCexConfig?.label} requires API passphrase`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      const previewData = {
        cex_name: selectedCex,
        account_label: accountLabel || `${selectedCexConfig?.label} Preview`,
        api_key: apiKey,
        api_secret: apiSecret,
        api_passphrase: requiresPassphrase ? apiPassphrase : null,
        timestamp: Date.now()
      };

      localStorage.setItem('cc_preview_api', JSON.stringify(previewData));
      
      setSuccess(true);
      
      setTimeout(() => {
        onClose();
        navigate('/preview/dashboard');
      }, 1000);
    } catch (err: any) {
      setError('Failed to establish initial sync simulation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/5 rounded-full blur-[40px] pointer-events-none" />
        
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-red-500 animate-pulse" />
            <h2 className="text-lg font-bold text-white tracking-tight">
              Live CEX Connection
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 font-sans">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-400 font-bold leading-normal">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-start gap-2">
              <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-bold text-green-400">
                  Syncing CEX account credentials...
                </p>
                <p className="text-[10px] text-green-500/70 mt-0.5">
                  Redirecting to live preview dashboard
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-black uppercase text-neutral-400 tracking-wider mb-2">
              Select Exchange *
            </label>
            <select
              value={selectedCex}
              onChange={(e) => {
                setSelectedCex(e.target.value);
                setApiPassphrase('');
                setError('');
              }}
              className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-red-500"
              required
            >
              <option value="">Choose an exchange...</option>
              {SUPPORTED_CEX.map((cex) => (
                <option key={cex.value} value={cex.value}>
                  {cex.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-black uppercase text-neutral-400 tracking-wider mb-2">
              Account Label
            </label>
            <input
              type="text"
              value={accountLabel}
              onChange={(e) => setAccountLabel(e.target.value)}
              placeholder="e.g. My Primary Binance"
              className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-xs text-white placeholder-neutral-500 focus:outline-none focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase text-neutral-400 tracking-wider mb-2">
              API Key *
            </label>
            <input
              type="text"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setError('');
              }}
              placeholder="Enter read-only API Key"
              className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-xs text-white placeholder-neutral-500 focus:outline-none focus:border-red-500 font-mono"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase text-neutral-400 tracking-wider mb-2">
              API Secret *
            </label>
            <input
              type="password"
              value={apiSecret}
              onChange={(e) => {
                setApiSecret(e.target.value);
                setError('');
              }}
              placeholder="Enter API Secret"
              className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-xs text-white placeholder-neutral-500 focus:outline-none focus:border-red-500 font-mono"
              required
            />
          </div>

          {requiresPassphrase && (
            <div>
              <label className="block text-xs font-black uppercase text-neutral-400 tracking-wider mb-2">
                API Passphrase *
              </label>
              <input
                type="password"
                value={apiPassphrase}
                onChange={(e) => {
                  setApiPassphrase(e.target.value);
                  setError('');
                }}
                placeholder="Enter API Passphrase"
                className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-xs text-white placeholder-neutral-500 focus:outline-none focus:border-red-500 font-mono"
                required
              />
              <p className="text-[10px] text-neutral-500 mt-1.5">
                This exchange requires a passphrase to connect.
              </p>
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading || success}
              className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white text-xs font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-red-950/20"
            >
              {isLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Establishing Secure Sync...
                </>
              ) : (
                'Sync & Preview Account'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApiSyncModal;
